import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@chakra-ui/react';

// Dropdown anclado por coordenadas reales (position:fixed) renderizado vía createPortal
// directo a document.body — evita el problema de Portal/Popover de Chakra anidados dentro
// de un Modal (o cualquier ancestro con overflow/stacking propio), que quedan atrapados
// detrás o recortados en vez de flotar sobre el contenido.
const AnchoredDropdown = ({ anchorRef, isOpen, onClose, children, minWidth, matchWidth = true }) => {
  const [coords, setCoords] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !anchorRef.current) {
      setCoords(null);
      return;
    }
    const update = () => {
      const r = anchorRef.current.getBoundingClientRect();
      setCoords({
        top: r.bottom + 6,
        left: r.left,
        width: r.width
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (!isOpen || !onClose) return;
    const handleOutside = (e) => {
      if (dropdownRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleOutside, true);
    return () => document.removeEventListener('mousedown', handleOutside, true);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !coords) return null;

  return createPortal(
    <Box
      ref={dropdownRef}
      position="fixed"
      top={`${coords.top}px`}
      left={`${coords.left}px`}
      w={matchWidth ? `${coords.width}px` : undefined}
      minW={minWidth}
      zIndex={9999}
      bg="white"
      border="1px solid"
      borderColor="cream.line"
      borderRadius="14px"
      boxShadow="0 8px 20px -6px rgba(43,39,35,0.25)"
      maxH="320px"
      overflowY="auto"
    >
      {children}
    </Box>,
    document.body
  );
};

export default AnchoredDropdown;
